import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, RefreshCw, User, Phone, MapPin, Package, ShieldCheck, Inbox, Trash2, Download,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BRAND, API } from "@/constants/brand";
import { lenisStore } from "@/lib/lenisStore";

const TOKEN_KEY = "climaoliver_admin_token";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-ocean-600 focus:ring-4 focus:ring-ocean-600/10";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ESTADOS = {
  pendiente: { label: "Pendiente", badge: "bg-amber-50 text-amber-700", btn: "border-amber-300 text-amber-700" },
  contactado: { label: "Contactado", badge: "bg-ocean-50 text-ocean-700", btn: "border-ocean-600/40 text-ocean-700" },
  cerrado: { label: "Cerrado", badge: "bg-emerald-50 text-emerald-700", btn: "border-emerald-300 text-emerald-700" },
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Sesión iniciada correctamente.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-navy/5 sm:p-10"
      data-testid="admin-login-card"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white">
        <Lock className="h-5 w-5" />
      </span>
      <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-navy">
        Panel de solicitudes
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        Acceso restringido al equipo de {BRAND.name}.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
        <div>
          <label className="mb-2 block text-sm font-semibold text-navy">Correo electrónico</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@climaoliver.com"
            className={inputCls}
            data-testid="admin-email-input"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-navy">Contraseña</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={inputCls}
            data-testid="admin-password-input"
          />
        </div>
        {error && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full rounded-full bg-ember px-6 py-3.5 text-sm font-semibold text-white shadow-xl shadow-ember/25 transition-all duration-300 hover:bg-ember-700 disabled:opacity-60"
        >
          {loading ? "Accediendo…" : "Acceder al panel"}
        </button>
      </form>
    </motion.div>
  );
};

const LeadCard = ({ lead, index, onChangeEstado, onDelete, onSaveNota }) => {
  const [saving, setSaving] = useState(false);
  const [nota, setNota] = useState(lead.nota || "");
  const estado = ESTADOS[lead.estado] ? lead.estado : "pendiente";

  const guardarNota = async () => {
    if (saving || nota.trim() === (lead.nota || "")) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/leads/${lead.id}/nota`,
        { nota: nota.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } }
      );
      onSaveNota(lead.id, nota.trim());
      toast.success("Nota guardada.");
    } catch {
      toast.error("No se pudo guardar la nota.");
    } finally {
      setSaving(false);
    }
  };

  const cambiar = async (nuevo) => {
    if (nuevo === estado || saving) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/leads/${lead.id}/estado`,
        { estado: nuevo },
        { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } }
      );
      onChangeEstado(lead.id, nuevo);
    } catch {
      toast.error("No se pudo actualizar el estado.");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar definitivamente la solicitud de ${lead.nombre}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      onDelete(lead.id);
      toast.success("Solicitud eliminada.");
    } catch {
      toast.error("No se pudo eliminar la solicitud.");
    } finally {
      setSaving(false);
    }
  };

  return (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4) }}
    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
    data-testid={`lead-card-${index}`}
  >
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy font-display text-xs font-bold text-white">
          <User className="h-4 w-4" />
        </span>
        <div>
          <p className="font-display text-base font-bold text-navy" data-testid={`lead-nombre-${index}`}>{lead.nombre}</p>
          <p className="text-xs text-slate-400">{formatDate(lead.created_at)}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${ESTADOS[estado].badge}`}
          data-testid={`lead-estado-${index}`}
        >
          {ESTADOS[estado].label}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
            lead.privacidad ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}
          data-testid={`lead-consent-${index}`}
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          RGPD: {lead.privacidad ? "Aceptado" : "No registrado"}
        </span>
      </div>
    </div>
    <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
      <div className="flex items-center gap-2">
        <Package className="h-4 w-4 shrink-0 text-ocean-600" />
        <span className="text-slate-600">{lead.numero_equipos} · Aparato comprado: <strong>{lead.aparato_comprado || "—"}</strong></span>
      </div>
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 shrink-0 text-ocean-600" />
        <a href={`tel:${lead.telefono}`} className="font-medium text-ocean-700 hover:underline" data-testid={`lead-telefono-${index}`}>{lead.telefono}</a>
      </div>
      <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-1">
        <MapPin className="h-4 w-4 shrink-0 text-ocean-600" />
        <span className="text-slate-600">{lead.calle}, {lead.ciudad} ({lead.codigo_postal})</span>
      </div>
    </dl>
    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
      <span className="text-xs font-medium text-slate-400">Marcar como:</span>
      {Object.entries(ESTADOS).map(([key, cfg]) => (
        <button
          key={key}
          onClick={() => cambiar(key)}
          disabled={saving || key === estado}
          data-testid={`lead-estado-btn-${key}-${index}`}
          className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 disabled:cursor-default ${
            key === estado
              ? `${cfg.badge} border-transparent`
              : `bg-white ${cfg.btn} hover:shadow-sm`
          } ${saving ? "opacity-60" : ""}`}
        >
          {cfg.label}
        </button>
      ))}
      <button
        onClick={eliminar}
        disabled={saving}
        data-testid={`lead-delete-btn-${index}`}
        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-red-600 transition-all duration-200 hover:bg-red-50 disabled:opacity-60"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Eliminar
      </button>
    </div>
    <div className="mt-4">
      <label className="mb-1.5 block text-xs font-semibold text-slate-500">
        Nota interna (solo visible en este panel)
      </label>
      <div className="flex gap-2">
        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Ej. Llamar el lunes · Precio acordado: 1.450 €"
          data-testid={`lead-nota-input-${index}`}
          className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-ocean-600 focus:ring-4 focus:ring-ocean-600/10"
        />
        <button
          onClick={guardarNota}
          disabled={saving || nota.trim() === (lead.nota || "")}
          data-testid={`lead-nota-save-${index}`}
          className="self-end rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Guardar
        </button>
      </div>
    </div>
  </motion.article>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("todas");

  const FILTROS = [
    { key: "todas", label: "Todas" },
    { key: "pendiente", label: "Pendientes" },
    { key: "contactado", label: "Contactadas" },
    { key: "cerrado", label: "Cerradas" },
  ];

  const estadoDe = (l) => (ESTADOS[l.estado] ? l.estado : "pendiente");
  const leadsFiltrados = leads
    ? filtro === "todas"
      ? leads
      : leads.filter((l) => estadoDe(l) === filtro)
    : [];
  const countDe = (key) =>
    leads ? (key === "todas" ? leads.length : leads.filter((l) => estadoDe(l) === key).length) : 0;

  const exportarCSV = () => {
    if (!leads || leads.length === 0) {
      toast.error("No hay solicitudes para exportar.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const cabecera = [
      "Fecha", "Nombre", "Equipos", "Aparato comprado", "Ciudad", "Calle",
      "Código postal", "Teléfono", "Estado", "Consentimiento RGPD", "Nota interna",
    ].map(esc).join(";");
    const filas = leads.map((l) =>
      [
        formatDate(l.created_at),
        l.nombre,
        l.numero_equipos,
        l.aparato_comprado,
        l.ciudad,
        l.calle,
        l.codigo_postal,
        l.telefono,
        ESTADOS[estadoDe(l)].label,
        l.privacidad ? "Aceptado" : "No registrado",
        l.nota || "",
      ].map(esc).join(";")
    );
    const csv = "\uFEFF" + [cabecera, ...filas].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const hoy = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `solicitudes-clima-oliver-${hoy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Archivo de solicitudes descargado.");
  };

  useEffect(() => {
    lenisStore.current?.scrollTo(0, { immediate: true });
  }, []);

  const fetchLeads = async (tk = token) => {
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setLeads(data);
      setError("");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        setError("No se pudieron cargar las solicitudes.");
      }
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  };

  return (
    <div data-testid="admin-page">
      <Navbar />
      <main className="min-h-[70vh] bg-ocean-50/50 pb-20 pt-32 lg:pt-40">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-12">
          {!token ? (
            <LoginForm onLogin={setToken} />
          ) : (
            <div data-testid="admin-dashboard">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
                    Solicitudes de presupuesto
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    {leads ? `${leads.length} solicitud${leads.length === 1 ? "" : "es"} registrada${leads.length === 1 ? "" : "s"}` : "Cargando…"}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={exportarCSV}
                    data-testid="admin-export-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-navy transition-colors hover:border-ocean-600/40 hover:text-ocean-700"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Exportar a Excel
                  </button>
                  <button
                    onClick={() => fetchLeads()}
                    data-testid="admin-refresh-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-navy transition-colors hover:border-ocean-600/40 hover:text-ocean-700"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Actualizar
                  </button>
                  <button
                    onClick={logout}
                    data-testid="admin-logout-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy-800"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Cerrar sesión
                  </button>
                </div>
              </div>

              {error && (
                <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="admin-error">
                  {error}
                </p>
              )}

              <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-filtros">
                {FILTROS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFiltro(f.key)}
                    data-testid={`admin-filtro-${f.key}`}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                      filtro === f.key
                        ? "border-transparent bg-navy text-white shadow-lg shadow-navy/20"
                        : "border-slate-200 bg-white text-slate-600 hover:border-ocean-600/40 hover:text-ocean-700"
                    }`}
                  >
                    {f.label} · {countDe(f.key)}
                  </button>
                ))}
              </div>

              <div className="mt-6 space-y-4">
                {leads && leadsFiltrados.length === 0 && (
                  <div className="flex flex-col items-center rounded-3xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center" data-testid="admin-empty-state">
                    <Inbox className="h-8 w-8 text-slate-300" />
                    <p className="mt-4 font-display text-lg font-bold text-navy">
                      {filtro === "todas" ? "Aún no hay solicitudes" : "No hay solicitudes con este estado"}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Cuando un cliente envíe el formulario de presupuesto, aparecerá aquí.
                    </p>
                  </div>
                )}
                {leadsFiltrados.map((lead, i) => (
                  <LeadCard
                    key={lead.id || i}
                    lead={lead}
                    index={i}
                    onChangeEstado={(id, estado) =>
                      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, estado } : l)))
                    }
                    onDelete={(id) => setLeads((ls) => ls.filter((l) => l.id !== id))}
                    onSaveNota={(id, nota) =>
                      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, nota } : l)))
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
